import React from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Card, CardContent, Typography, Grid, Chip } from "@material-ui/core";
import { TextClip } from "frame/components";
import { createRowSelector, formatDate, useTerminology } from "utils";
import { getTaskStatusLabel, isTaskActive, normalizeTaskStatus } from "../task-status";

const useStyles = makeStyles((theme) => ({
	card: {
		display: "flex",
		flexWrap: "wrap",
		position: "relative",
		marginBottom: theme.spacing(2),
	},
	container: {
		margin: theme.spacing(0.5),
	},
	content: {
		padding: theme.spacing(2),
		flexGrow: 1,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		[theme.breakpoints.down("sm")]: {
			width: "100%",
			flexWrap: "wrap",
		},
		"&:last-child": {
			paddingBottom: theme.spacing(2),
		},
	},
	label: {
		padding: theme.spacing(0, 0, 0.5, 0),
		color: theme.palette.text.tertiary,
	},
	value: {
		textOverflow: "ellipsis",
		overflow: "hidden",
		padding: theme.spacing(0, 0, 0.5, 0),
	},
	valueInactive: {
		color: theme.palette.text.tertiary,
	},
	success: {
		backgroundColor: theme.palette.success.main,
		color: theme.palette.white,
	},
	error: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.white,
	},
}));

const getStatusChipClass = (classes, status) => {
	const normalized = normalizeTaskStatus(status);
	if (normalized === "Done") return classes.success;
	if (normalized === "Cancelled") return classes.error;
	return null;
};

const getCreatedByDisplay = (createdBy, staffList) => {
	if (!createdBy) return "-";

	const normalize = (v) => String(v || "").trim().toLowerCase();
	const createdByNormalized = normalize(createdBy);

	const staffMatch =
		Array.isArray(staffList) &&
		staffList.find((s) => {
			const staffEmail =
				s?.email || s?.emailAddress || s?.userName || s?.user?.userName;
			return normalize(staffEmail) === createdByNormalized;
		});

	if (staffMatch) {
		const fullName =
			staffMatch.fullName ||
			`${staffMatch.firstName || ""} ${staffMatch.lastName || ""}`.trim();
		return fullName || createdBy;
	}

	return createdBy;
};

const getClosedDate = (task) => {
	const status = normalizeTaskStatus(task?.status);
	if (status === "Done" && task?.completedUTC) return task.completedUTC;
	if (status === "Cancelled" && task?.lastModifiedUTC) return task.lastModifiedUTC;
	return null;
};

const getClosedDateLabel = (status) =>
	normalizeTaskStatus(status) === "Cancelled" ? "Cancelled Date" : "Completed Date";

const TaskListing = ({ task, className, showStaff, staffList, onEdit, ...rest }) => {
	const classes = useStyles();
	const { siteLabel } = useTerminology();
	const active = isTaskActive(task.status);
	const valueStyle = clsx(classes.value, !active && classes.valueInactive);
	const statusChipClass = getStatusChipClass(classes, task.status);
	const createdByDisplay = getCreatedByDisplay(task.createdBy, staffList);
	const closedDate = getClosedDate(task);
	const closedDateLabel = getClosedDateLabel(task.status);

	const titleCols = showStaff ? 3 : 4;
	const staffCols = 2;
	const siteCols = showStaff ? 2 : 3;
	const notesCols = 3;
	const statusCols = 1;

	const onRowSelect = createRowSelector(() => onEdit(task));

	return (
		<Card
			{...rest}
			data-cy={`task-${task.id}`}
			className={clsx(classes.card, className)}
			onClick={onRowSelect}
			style={{ cursor: "pointer" }}
		>
			<CardContent className={classes.content}>
				<Grid container spacing={2} className={classes.container}>
					<Grid item lg={titleCols} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Title
						</Typography>
						<Typography variant="h6" className={valueStyle}>
							<TextClip hideSeeMore text={task.title || "-"} numberOfCharacters={60} />
						</Typography>
					</Grid>
					{showStaff && (
						<Grid item lg={staffCols} md={6} xs={12}>
							<Typography className={classes.label} variant="body2">
								Staff
							</Typography>
							<Typography variant="h6" className={valueStyle}>
								<TextClip
									hideSeeMore
									text={task.staffFullName || "-"}
									numberOfCharacters={60}
								/>
							</Typography>
						</Grid>
					)}
					<Grid item lg={siteCols} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							{siteLabel}
						</Typography>
						<Typography variant="h6" className={valueStyle}>
							<TextClip hideSeeMore text={task.siteName || "-"} numberOfCharacters={60} />
						</Typography>
					</Grid>
					<Grid item lg={notesCols} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Notes
						</Typography>
						<Typography variant="h6" className={valueStyle}>
							<TextClip hideSeeMore text={task.notes || "-"} numberOfCharacters={120} />
						</Typography>
					</Grid>
					<Grid item lg={statusCols} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Status
						</Typography>
						<Chip
							className={statusChipClass}
							size="small"
							label={getTaskStatusLabel(task.status)}
						/>
					</Grid>
					<Grid item lg={titleCols} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Created By
						</Typography>
						<Typography variant="h6" className={valueStyle}>
							{createdByDisplay}
						</Typography>
					</Grid>
					{showStaff && (
						<Grid item lg={staffCols} md={6} xs={12}>
							<Typography className={classes.label} variant="body2">
								Created Date
							</Typography>
							<Typography variant="h6" className={valueStyle}>
								{task.createdUTC ? formatDate(task.createdUTC) : "-"}
							</Typography>
						</Grid>
					)}
					<Grid item lg={siteCols} md={6} xs={12}>
						{!showStaff && (
							<>
								<Typography className={classes.label} variant="body2">
									Created Date
								</Typography>
								<Typography variant="h6" className={valueStyle}>
									{task.createdUTC ? formatDate(task.createdUTC) : "-"}
								</Typography>
							</>
						)}
						{showStaff && closedDate && (
							<>
								<Typography className={classes.label} variant="body2">
									{closedDateLabel}
								</Typography>
								<Typography variant="h6" className={valueStyle}>
									{formatDate(closedDate)}
								</Typography>
							</>
						)}
					</Grid>
					{!showStaff && closedDate && (
						<Grid item lg={notesCols} md={6} xs={12}>
							<Typography className={classes.label} variant="body2">
								{closedDateLabel}
							</Typography>
							<Typography variant="h6" className={valueStyle}>
								{formatDate(closedDate)}
							</Typography>
						</Grid>
					)}
				</Grid>
			</CardContent>
		</Card>
	);
};

TaskListing.propTypes = {
	task: PropTypes.object.isRequired,
	className: PropTypes.string,
	showStaff: PropTypes.bool,
	staffList: PropTypes.array,
	onEdit: PropTypes.func.isRequired,
};

export default TaskListing;
